import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { ChevronLeft, Eye, Package, Plus, TrendingUp } from 'lucide-react';
import LoadingSpinner from '../LoadingSpinner';
import BackToHomeLink from '../BackToHomeLink';
import ProductResultsToolbar from '../ProductResultsToolbar';
import SellerListingRow from './SellerListingRow';
import ConfirmModal from '../ConfirmModal';
import { useTranslation } from '../../context/TranslationContext';
import { translateWithFallback } from '../../utils/translateWithFallback';
import { deleteMyProduct, getMyProducts, getMySales } from '../../services/api.shop';
import { Order, OrderStatus, PaymentStatus, Product, ProductStatus } from '../../types';
import { useProtectedQuery } from '../../hooks/useProtectedQuery';
import { useFrontendAuth } from '../../hooks/useAuth';
import { invalidateSellerListingCaches } from '../../utils/invalidateSellerListingCaches';
import {
    DEFAULT_PRODUCT_SORT,
    ProductSortKey,
    sortProductsClient,
} from '../../utils/productSort';
import { formatMoney } from '../../utils/productValidation';

type ListFilter = 'all' | 'active' | 'sold' | 'draft';

/** Statuses that never count as earned revenue (unpaid holds, cancelled, money returned). */
const REVENUE_EXCLUDED = new Set<string>([
    OrderStatus.Pending,
    OrderStatus.Cancelled,
    OrderStatus.Refunded,
]);

function isSold(p: Product) {
    return !!p.isSold || p.status === ProductStatus.Sold;
}

function isDraft(p: Product) {
    return p.status === ProductStatus.PendingReview || p.status === ProductStatus.Rejected;
}

function isActive(p: Product) {
    return !isSold(p) && p.status === ProductStatus.Active;
}

/** Paid-through-completed sales; Problem (open return request) still counts until Refunded. */
function orderCountsTowardRevenue(order: Order): boolean {
    const status = String(order.status ?? '');
    if (REVENUE_EXCLUDED.has(status)) return false;
    // Defence in depth: payment marked refunded even if status lag
    if (String(order.paymentStatus ?? '') === PaymentStatus.Refunded) return false;
    return true;
}

function sumSellerRevenue(orders: Order[]): number {
    return orders.reduce((sum, o) => {
        if (!orderCountsTowardRevenue(o)) return sum;
        const total = Number(o.total);
        return sum + (Number.isFinite(total) ? total : 0);
    }, 0);
}

const AccountProductsSection = () => {
    const { t } = useTranslation();
    const tr = (key: string, fb: string) => translateWithFallback(t, key, fb);
    const queryClient = useQueryClient();
    const { user } = useFrontendAuth();
    const [filter, setFilter] = useState<ListFilter>('all');
    const [sortKey, setSortKey] = useState<ProductSortKey>(DEFAULT_PRODUCT_SORT);
    const [deleteTargetId, setDeleteTargetId] = useState<number | null>(null);
    const [deleteBusy, setDeleteBusy] = useState(false);
    const [deleteError, setDeleteError] = useState<string | null>(null);

    const {
        data: products = [],
        isLoading: productsLoading,
        error,
    } = useProtectedQuery<Product[]>({
        queryKey: ['myProducts'],
        queryFn: getMyProducts,
        staleTime: 30 * 1000,
    });

    // Same key as AccountSalesSection — share React Query cache, no second store
    const {
        data: sales = [],
        isLoading: salesLoading,
        isFetched: salesFetched,
    } = useProtectedQuery<Order[]>({
        queryKey: ['mySales'],
        queryFn: getMySales,
        staleTime: 30 * 1000,
    });

    // useProtectedQuery keeps refetchOnMount false — refresh private + public listing caches on open
    useEffect(() => {
        void invalidateSellerListingCaches(queryClient, user?.id);
    }, [queryClient, user?.id]);

    // Wait for both so revenue is ready on first paint (parallel fetch, shared cache with Sales)
    const isLoading = productsLoading || salesLoading;

    const stats = useMemo(() => {
        const active = products.filter(isActive).length;
        const sold = products.filter(isSold).length;
        const views = products.reduce((sum, p) => sum + (p.viewCount || 0), 0);
        // Revenue from real order totals; Refunded/Cancelled/Pending excluded (no separate subtract)
        const revenue = salesFetched ? sumSellerRevenue(sales) : null;
        return { active, sold, views, revenue };
    }, [products, sales, salesFetched]);

    // Status filter first, then shared client sort (full list already loaded — no server cache)
    const filtered = useMemo(() => {
        let list = products;
        if (filter === 'active') list = products.filter(isActive);
        else if (filter === 'sold') list = products.filter(isSold);
        else if (filter === 'draft') list = products.filter(isDraft);
        return sortProductsClient(list, sortKey);
    }, [products, filter, sortKey]);

    const handleDeleteProduct = (productId: number) => {
        setDeleteError(null);
        setDeleteTargetId(productId);
    };

    const confirmDeleteProduct = async () => {
        if (deleteTargetId == null) return;
        setDeleteBusy(true);
        setDeleteError(null);
        try {
            await deleteMyProduct(deleteTargetId);
            await invalidateSellerListingCaches(queryClient, user?.id);
            setDeleteTargetId(null);
        } catch (err) {
            console.error('Failed to delete product:', err);
            const axiosErr = err as AxiosError<{ errors?: { General?: string }; message?: string }>;
            const apiMsg =
                axiosErr.response?.data?.errors?.General ||
                axiosErr.response?.data?.message;
            setDeleteError(
                apiMsg || t('account.myproducts.deletefailed') || 'Delete failed'
            );
        } finally {
            setDeleteBusy(false);
        }
    };

    if (isLoading) return <LoadingSpinner />;
    if (error) {
        return (
            <p className="rounded-lg bg-red-50 px-4 py-3 text-red-700">
                {t('account.hub.loaderror') || 'Unable to load listings.'}
            </p>
        );
    }

    const filterPills: { id: ListFilter; label: string; count?: number }[] = [
        { id: 'all', label: tr('account.listings.filter.all', 'All') },
        { id: 'active', label: tr('account.listings.filter.active', 'Active'), count: stats.active },
        { id: 'sold', label: tr('account.listings.filter.sold', 'Sold'), count: stats.sold },
        {
            id: 'draft',
            label: tr('account.listings.filter.draft', 'Draft'),
            count: products.filter(isDraft).length,
        },
    ];

    return (
        <div className="mx-auto max-w-4xl py-4 sm:py-8">
            <BackToHomeLink />
            <Link
                to="/my-account"
                className="mb-6 inline-flex min-h-11 items-center gap-1 text-sm text-[color-mix(in_srgb,var(--text-color),transparent_30%)] hover:underline"
            >
                <ChevronLeft className="h-4 w-4" aria-hidden />
                {t('account.hub.back') || 'Back to account'}
            </Link>

            <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-[var(--text-color)] sm:text-3xl">
                        {tr('account.listings.title', 'My Listings')}
                    </h1>
                    <p className="mt-1 text-sm text-[color-mix(in_srgb,var(--text-color),transparent_40%)]">
                        {tr('account.listings.subtitle', 'Manage the items you are selling')}
                    </p>
                </div>
                <Link
                    to="/sell"
                    className="inline-flex min-h-11 items-center gap-2 rounded-full bg-[var(--accent-color)] px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:opacity-90"
                >
                    <Plus className="h-4 w-4" aria-hidden />
                    {tr('account.listings.add', 'Add listing')}
                </Link>
            </div>

            {/* Stat chips */}
            <div className="mb-6 grid grid-cols-2 gap-3 sm:grid-cols-4">
                {[
                    {
                        icon: <Package className="h-4 w-4" />,
                        value: String(stats.active),
                        label: tr('account.listings.stat.active', 'Active'),
                    },
                    {
                        icon: <Package className="h-4 w-4 opacity-70" />,
                        value: String(stats.sold),
                        label: tr('account.listings.stat.sold', 'Sold'),
                    },
                    {
                        icon: <Eye className="h-4 w-4" />,
                        value: String(stats.views),
                        label: tr('account.listings.stat.views', 'Total views'),
                    },
                    {
                        icon: <TrendingUp className="h-4 w-4" />,
                        value: stats.revenue != null ? `€${formatMoney(stats.revenue)}` : '—',
                        label: tr('account.listings.stat.revenue', 'Revenue'),
                    },
                ].map((s) => (
                    <div
                        key={s.label}
                        className="rounded-2xl border border-[color-mix(in_srgb,var(--accent-color),transparent_55%)] bg-[color-mix(in_srgb,var(--bg-color),white_35%)] px-4 py-3 shadow-sm"
                    >
                        <div className="mb-1 text-[var(--text-color)] opacity-60">{s.icon}</div>
                        <p className="text-xl font-bold text-[var(--text-color)]">{s.value}</p>
                        <p className="text-xs text-[color-mix(in_srgb,var(--text-color),transparent_40%)]">{s.label}</p>
                    </div>
                ))}
            </div>

            {/* Status filters */}
            <div className="mb-4 flex flex-wrap gap-2">
                {filterPills.map((p) => {
                    const active = filter === p.id;
                    return (
                        <button
                            key={p.id}
                            type="button"
                            onClick={() => setFilter(p.id)}
                            className={`inline-flex min-h-10 items-center gap-1.5 rounded-full px-4 py-1.5 text-sm font-medium transition ${
                                active
                                    ? 'bg-[var(--accent-color)] text-white shadow-sm'
                                    : 'bg-[color-mix(in_srgb,var(--bg-color),white_30%)] text-[color-mix(in_srgb,var(--text-color),transparent_25%)] hover:bg-[color-mix(in_srgb,var(--accent-color),transparent_75%)]'
                            }`}
                        >
                            {p.label}
                            {p.count != null && (
                                <span className={`text-xs ${active ? 'opacity-80' : 'opacity-60'}`}>{p.count}</span>
                            )}
                        </button>
                    );
                })}
            </div>

            <ProductResultsToolbar
                count={filtered.length}
                sort={sortKey}
                onSortChange={setSortKey}
            />

            {filtered.length === 0 ? (
                <div className="rounded-2xl border border-dashed border-[color-mix(in_srgb,var(--accent-color),transparent_50%)] px-6 py-12 text-center">
                    <p className="text-[color-mix(in_srgb,var(--text-color),transparent_35%)]">
                        {tr('account.listings.empty', 'No listings in this filter.')}
                    </p>
                    <Link
                        to="/sell"
                        className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-[var(--text-color)] underline"
                    >
                        <Plus className="h-4 w-4" />
                        {tr('account.listings.add', 'Add listing')}
                    </Link>
                </div>
            ) : (
                <ul className="flex flex-col gap-3">
                    {filtered.map((product) => (
                        <li key={product.id}>
                            <SellerListingRow product={product} onDelete={handleDeleteProduct} />
                        </li>
                    ))}
                </ul>
            )}

            <ConfirmModal
                open={deleteTargetId != null}
                title={tr('account.myproducts.deletetitle', 'Delete listing')}
                message={
                    deleteError ||
                    (t('account.myproducts.deleteconfirm') ||
                        'Delete this listing? This cannot be undone.')
                }
                confirmLabel={tr('common.delete', 'Delete')}
                danger
                busy={deleteBusy}
                onConfirm={() => void confirmDeleteProduct()}
                onCancel={() => {
                    if (!deleteBusy) {
                        setDeleteTargetId(null);
                        setDeleteError(null);
                    }
                }}
            />
        </div>
    );
};

export default AccountProductsSection;
